import { Box, BoxProps, FormHelperText } from "@mui/material";
import { useMemo } from "react";

import { renderIf } from "@/common/helpers/render/renderIf";
import { BaseEntityDto, EntityType } from "@/core/api/generated/v1";

import AccessoryLink from "../../Accessory/AccessoryLink";
import AccessoryCheckLink from "../../AccessoryCheck/AccessoryCheckLink";
import AssessmentFlowLink from "../../AssessmentFlow/AssessmentFlowLink";
import AssetLink from "../../Asset/AssetLink";
import AssetSubscriptionLink from "../../AssetSubscription/AssetSubscriptionLink";
import AssetSubscriptionPlanLink from "../../AssetSubscriptionPlan/AssetSubscriptionPlanLink";
import ChargeLink from "../../Charge/ChargeLink";
import ContractLink from "../../Contract/ContractLink";
import CustomerLink from "../../Customer/CustomerLink";
import DamageCostEvaluationLink from "../../DamageCostEvaluation/DamageCostEvaluationLink";
import DamageCostEvaluationAggregateLink from "../../DamageCostEvaluationAggregate/DamageCostEvaluationAggregateLink";
import DamageDetectionLink from "../../DamageDetection/DamageDetectionLink";
import DamageDetectionAggregateLink from "../../DamageDetectionAggregate/DamageDetectionAggregateLink";
import DepartmentLink from "../../Department/DepartmentLink";
import GeneralStatusHistoryLink from "../../GeneralStatus/GeneralStatusHistory/GeneralStatusHistoryLink";
import LocationLink from "../../Location/LocationLink";
import MaintenanceLink from "../../Maintenance/MaintenanceLink";
import ProductLocationLink from "../../ProductLocation/ProductLocationLink";
import RepairOperationLink from "../../RepairOperation/RepairOperationLink";
import SpotLink from "../../Spot/SpotLink";
import SupplierLink from "../../Supplier/SupplierLink";
import TenantLink from "../../Tenant/TenantLink";
import TenantConnectionLink from "../../TenantConnection/TenantConnectionLink";
import TenantConnectionRequestLink from "../../TenantConnectionRequest/TenantConnectionRequestLink";
import TenantRequestLink from "../../TenantRequest/TenantRequestLink";
import VehicleLink from "../../Vehicle/VehicleLink";
import VehicleDamageLink from "../../VehicleDamage/VehicleDamageLink";
import VisualInspectionLink from "../../VisualInspection/VisualInspectionLink";
import WashLink from "../../Wash/WashLink";
import WheelOperationLink from "../../WheelOperation/WheelOperationLink";
import { BaseEntityLinkInheritableProps } from "./BaseEntityLink";

export interface EntityLinkProps {
  entityType: EntityType | undefined;
  entityId: string | undefined;
  entity: BaseEntityDto | undefined;
  linkProps?: Partial<BaseEntityLinkInheritableProps>;
  sx?: BoxProps["sx"];
}

/** Based on entity type renders corresponding entity component. */
export default function EntityLink({
  entityType,
  entityId,
  entity,
  linkProps,
  sx,
}: EntityLinkProps) {
  const entityIdComputed = useMemo(() => entityId ?? entity?.id, [entityId, entity]);

  if (!entityType && !entityIdComputed) {
    return null;
  }

  return (
    <Box
      component='span'
      sx={{
        display: "inline-block",
        ...sx,
      }}
    >
      {renderIf()
        .if(entityType === EntityType.Company)
        .then(<TenantLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Department)
        .then(<DepartmentLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Location)
        .then(<LocationLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Vehicle)
        .then(<VehicleLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.VehicleDamage)
        .then(
          <VehicleDamageLink
            vehicleId={undefined}
            entity={entity}
            entityId={entityId}
            {...linkProps}
          />,
        )
        .elseif(entityType === EntityType.Contract)
        .then(<ContractLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.AssessmentFlow)
        .then(<AssessmentFlowLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Accessory)
        .then(<AccessoryLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.AccessoryCheck)
        .then(<AccessoryCheckLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.VisualInspection)
        .then(<VisualInspectionLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.DamageDetection)
        .then(<DamageDetectionLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.DamageDetectionAggregate)
        .then(<DamageDetectionAggregateLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.DamageCostEvaluation)
        .then(<DamageCostEvaluationLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.DamageCostEvaluationAggregate)
        .then(
          <DamageCostEvaluationAggregateLink entity={entity} entityId={entityId} {...linkProps} />,
        )
        .elseif(entityType === EntityType.RepairOperation)
        .then(<RepairOperationLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Wash)
        .then(<WashLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.WheelOperation)
        .then(<WheelOperationLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Wash)
        .then(<WashLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Maintenance)
        .then(<MaintenanceLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Asset)
        .then(<AssetLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.ProductLocation)
        .then(<ProductLocationLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.AssetSubscriptionPlan)
        .then(<AssetSubscriptionPlanLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.AssetSubscription)
        .then(<AssetSubscriptionLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.TenantConnectionRequest)
        .then(<TenantConnectionRequestLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.TenantConnection)
        .then(<TenantConnectionLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.TenantRequest)
        .then(<TenantRequestLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Spot)
        .then(<SpotLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Customer)
        .then(<CustomerLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Supplier)
        .then(<SupplierLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.Charge)
        .then(<ChargeLink entity={entity} entityId={entityId} {...linkProps} />)
        .elseif(entityType === EntityType.GeneralStatusHistory)
        .then(<GeneralStatusHistoryLink entity={entity} entityId={entityId} {...linkProps} />)
        .else(<FormHelperText error>Unknown entity {entityType}</FormHelperText>)
        .render()}
    </Box>
  );
}
